import classNames from "classnames"
import React from "react"

import "./style.css"

export interface SelectItem {
    key: string | number
    value: string | number
    logo?: string
}

interface SelectProps {
    options: SelectItem[]
    value: string
    style?: React.CSSProperties
    small?: boolean
    className?: string
    onChange(value: string | number): void
}

function NodeCompare(node: Node, className: string): boolean {
    // @ts-ignore
    if (node.className?.includes?.(className)) {
        return true
    } else if (node.parentNode) {
        return NodeCompare(node.parentNode, className)
    }
    return false
}

function Select({ value, options, onChange, style, small, className }: SelectProps) {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const down = React.useRef(false)

    const [selectFocus, setFocus] = React.useState(false)
    const [show, setShow] = React.useState(false)
    const [fade, setFade] = React.useState(false)

    const logo = React.useMemo(() => {
        if (value && !!options.length) {
            return options.find(({ key }) => key === value)?.logo
        }
    }, [value, options])

    React.useEffect(() => {
        document.addEventListener("mousedown", (event: any) => {
            if (NodeCompare(event.target, "rellpay-select-option")) {
                down.current = true
            }
        })
        document.addEventListener("mouseup", () => {
            down.current = false
        })
        return () => {
            document.removeEventListener("mousedown", () => { })
            document.removeEventListener("mouseup", () => { })
        }
    }, [])

    const onBlur = () => {
        if (down.current) return
        setFocus(false)
        setFade(false)
        setTimeout(() => {
            setShow(false)
        }, 150)
    }

    const onFocus = () => {
        setFocus(true)
        if (show) {
            setFade(false)
            setTimeout(() => {
                setShow(false)
            }, 150)
        }
        else {
            setShow(true)
            setFade(true)
            inputRef.current?.focus()
        }
    }

    const selectItem = (item: string | number) => {
        onChange(item)
        onBlur()
    }

    return (
        <React.Fragment>
            <div
                className="rellpay-select"
                style={style}
            >
                <div
                    onClick={onFocus}
                    className={`rellpay-select-content${selectFocus ? " rellpay-select-focus" : ""}${show ? " rellpay-select-show" : ""} ${className && className}`}
                    css={{ height: small ? 32 : 42, padding: small ? "0 8px" : "0 12px" }}
                >
                    {logo && <img width={14} height={14} style={{ marginRight: 6 }} src={logo} />}
                    <input
                        className="rellpay-select-input"
                        value={value}
                        ref={inputRef}
                        onBlur={onBlur}
                        readOnly
                    />
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.29289 7.29289L3.70711 5.70711C3.07714 5.07714 3.52331 4 4.41421 4H7.58579C8.47669 4 8.92286 5.07714 8.29289 5.70711L6.70711 7.29289C6.31658 7.68342 5.68342 7.68342 5.29289 7.29289Z" fill="#262626" />
                    </svg>
                </div>
            </div>
            {show && <div className={classNames("mask", fade ? "rellpay-select-option-fadeIn" : "rellpay-select-option-fadeOut")} />}
            {show && (
                <div
                    className={classNames("rellpay-select-option", fade ? "rellpay-select-option-fadeIn" : "rellpay-select-option-fadeOut")}
                >
                    <div className="rellpay-select-option-btn">
                        <span>Select Chain</span>
                        <svg onClick={() => setShow(false)} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 6L18 18" stroke="black" strokeWidth="1" />
                            <path d="M6 18L18 6" stroke="black" strokeWidth="1" />
                        </svg>
                    </div>
                    <div className="rellpay-select-option-content">
                        {options.map(item => (
                            <div
                                onClick={selectItem.bind(void 0, item.key)}
                                key={item.key}
                                className={`rellpay-select-option-item${value === item.key ? " active" : ""}`}
                            >
                                {item.logo && <img src={item.logo} />}
                                {item.value}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </React.Fragment>
    )
}

export default Select
